import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import { isFeatureEnabled } from '../utils/features';

type PostLayoutSize =
  | 'mobile'
  | 'narrow'
  | 'small'
  | 'regular'
  | 'wide'
  | 'xWide';

const PostLayoutSizeContext = React.createContext<PostLayoutSize>('mobile');

interface Breakpoints {
  narrow: number;
  small: number;
  regular: number;
  wide: number;
  xWide: number;
}

function getPostLayoutSize(
  currentSize: number,
  { narrow, small, regular, wide, xWide }: Breakpoints,
): PostLayoutSize {
  if (currentSize < narrow) return 'mobile';
  if (currentSize < small) return 'narrow';
  if (currentSize < regular) return 'small';
  if (currentSize < wide) return 'regular';
  if (currentSize < xWide) return 'wide';
  return 'xWide';
}

interface LegacyPostLayoutSizeProviderProps {
  children: React.ReactNode;
}

// The default implementation is replicating the layout sizes from post.scss
// If you change the breakpoints here, also change them in post.scss
export const LegacyPostLayoutSizeProvider = ({
  children,
}: LegacyPostLayoutSizeProviderProps) => {
  const [currentSize, setCurrentSize] = useState(() => window.innerWidth);
  const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
  const isRightPanelOpen = useAppSelector((state) => state.ui.rightPanel.open);

  useEffect(() => {
    const handleResize = () => {
      setCurrentSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const postLayoutSize = useMemo(() => {
    if (isRightPanelOpen && isSidebarOpen) {
      return getPostLayoutSize(currentSize, {
        narrow: 768,
        small: 1280,
        regular: 1440,
        wide: 1600,
        xWide: 1920,
      });
    }

    if (isSidebarOpen || isRightPanelOpen) {
      return getPostLayoutSize(currentSize, {
        narrow: 768,
        small: 992,
        regular: 1280,
        wide: 1440,
        xWide: 1600,
      });
    }

    return getPostLayoutSize(currentSize, {
      narrow: 768,
      small: 768,
      regular: 1024,
      wide: 1280,
      xWide: 1440,
    });
  }, [isRightPanelOpen, isSidebarOpen, currentSize]);

  return (
    <PostLayoutSizeContext.Provider value={postLayoutSize}>
      {children}
    </PostLayoutSizeContext.Provider>
  );
};

export const PostLayoutSizeProvider = ({
  containerRef,
  children,
}: NewPostLayoutSizeProviderProps) => {
  if (isFeatureEnabled('new_post_layout')) {
    return (
      <NewPostLayoutSizeProvider containerRef={containerRef}>
        {children}
      </NewPostLayoutSizeProvider>
    );
  }

  return (
    <LegacyPostLayoutSizeProvider>{children}</LegacyPostLayoutSizeProvider>
  );
};

interface NewPostLayoutSizeProviderProps {
  containerRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
}

function getPostLayoutSizeFromWidth(width: number): PostLayoutSize {
  if (width < 1024) return 'narrow';
  if (width < 1280) return 'small';
  if (width < 1440) return 'regular';
  return 'wide';
}

const NewPostLayoutSizeProvider = ({
  containerRef,
  children,
}: NewPostLayoutSizeProviderProps) => {
  const [postLayoutSize, setPostLayoutSize] =
    useState<PostLayoutSize>('narrow');

  useLayoutEffect(() => {
    if (!containerRef.current) return undefined;

    function measurePostLayoutSize(container: HTMLElement) {
      setPostLayoutSize(
        getPostLayoutSizeFromWidth(container.getBoundingClientRect().width),
      );
    }

    measurePostLayoutSize(containerRef.current);
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === containerRef.current) {
          measurePostLayoutSize(containerRef.current);
        }
      });
    });
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [containerRef]);

  return (
    <PostLayoutSizeContext.Provider value={postLayoutSize}>
      {children}
    </PostLayoutSizeContext.Provider>
  );
};

export const usePostLayoutSize = () => useContext(PostLayoutSizeContext);
